import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { ArrowLeft, DollarSign, TrendingUp, Award, ChevronUp, ChevronDown } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { tenantTodayDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency } from '../../lib/display-labels';

type SortField = 'revenue' | 'utilization';
type SortDir = 'asc' | 'desc';

const ASSET_TYPES = ['All', 'Frac Tank', 'Generator', 'Light Tower', 'Office Trailer'];

/** Whole-dollar money. @see fmtCurrency — absence renders '—', not '$0'. */
const fmt = (n: number | string | null | undefined) => fmtCurrency(n, null, 0);

function UtilPill({ pct }: { pct: number }) {
  const color = pct >= 70 ? 'bg-green-500/20 text-green-400' : pct >= 40 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>{pct.toFixed(0)}%</span>;
}

export default function RevenueByAssetReport() {
  const navigate = useNavigate();
  // The TENANT's current month (companies.time_zone), not the device's. No
  // zone, no seeded range — the inputs start blank rather than guess.
  const timeZone = useTenantTimeZone();
  const now = tenantTodayDate(timeZone);
  const [startDate, setStartDate] = useState(now ? format(startOfMonth(now), 'yyyy-MM-dd') : '');
  const [endDate, setEndDate] = useState(now ? format(endOfMonth(now), 'yyyy-MM-dd') : '');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortField, setSortField] = useState<SortField>('revenue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data, isLoading } = useQuery({
    queryKey: ['revenue-by-asset', startDate, endDate],
    queryFn: () => apiClient.get('/surface-rentals/reports/revenue-by-asset', { params: { start_date: startDate, end_date: endDate } }).then(r => unwrap<any>(r.data)),
  });

  const assets: any[] = useMemo(() => {
    let list = data?.assets || [];
    if (typeFilter !== 'All') {
      list = list.filter((a: any) => (a.asset_type || '').toLowerCase() === typeFilter.toLowerCase());
    }
    list = [...list].sort((a: any, b: any) => {
      const va = sortField === 'revenue' ? (a.total_revenue || 0) : (a.utilization_pct || 0);
      const vb = sortField === 'revenue' ? (b.total_revenue || 0) : (b.utilization_pct || 0);
      return sortDir === 'desc' ? vb - va : va - vb;
    });
    return list;
  }, [data, typeFilter, sortField, sortDir]);

  // API: { period, assets, totals: { total_revenue, avg_utilization_pct } }.
  // The old code read data.total_revenue / .avg_utilization / .top_asset off the
  // top level, and a.revenue / a.utilization off each row — none of which the
  // endpoint sends. Revenue and utilization survived on their row fallbacks;
  // those row fallbacks were reading the wrong keys too, so both were 0.
  const totalRevenue = data?.totals?.total_revenue ?? assets.reduce((s: number, a: any) => s + (a.total_revenue || 0), 0);
  const avgUtil = data?.totals?.avg_utilization_pct ?? (assets.length > 0 ? assets.reduce((s: number, a: any) => s + (a.utilization_pct || 0), 0) / assets.length : 0);
  const topAsset = assets.length > 0 ? assets[0]?.asset_name || '—' : '—';

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'desc' ? <ChevronDown size={12} className="inline" /> : <ChevronUp size={12} className="inline" />;
  };

  const isEmpty = !isLoading && assets.length === 0;

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Revenue by Asset</h1>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent" />
            <span className="text-fieldiq-muted text-xs">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent" />
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {ASSET_TYPES.map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${typeFilter === t ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-muted hover:text-white'}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <DollarSign className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-lg font-bold text-fieldiq-accent">{fmt(totalRevenue)}</p>
            <p className="text-[10px] text-fieldiq-muted">Total Revenue</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <TrendingUp className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{avgUtil.toFixed(0)}%</p>
            <p className="text-[10px] text-fieldiq-muted">Avg Utilization</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <Award className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-sm font-bold text-white truncate">{topAsset}</p>
            <p className="text-[10px] text-fieldiq-muted">Top Asset</p>
          </div>
        </div>

        {isEmpty ? (
          <div className="text-center py-16">
            <DollarSign size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-muted text-sm">No asset revenue data for this period.</p>
          </div>
        ) : (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs">
                    <th className="text-left px-4 py-2.5 font-medium">Asset</th>
                    <th className="text-left px-4 py-2.5 font-medium">Type</th>
                    <th className="text-right px-4 py-2.5 font-medium">Days on Rent</th>
                    <th className="text-right px-4 py-2.5 font-medium cursor-pointer select-none" onClick={() => toggleSort('utilization')}>Utilization <SortIcon field="utilization" /></th>
                    <th className="text-right px-4 py-2.5 font-medium cursor-pointer select-none" onClick={() => toggleSort('revenue')}>Revenue <SortIcon field="revenue" /></th>
                    <th className="text-right px-4 py-2.5 font-medium">Avg Daily Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((a: any, i: number) => (
                    <tr key={i} className="border-b border-fieldiq-border/50">
                      <td className="px-4 py-2.5 text-white font-medium">{a.asset_name}</td>
                      <td className="px-4 py-2.5 text-fieldiq-muted">{a.asset_type}</td>
                      <td className="px-4 py-2.5 text-right text-fieldiq-muted">{a.days_on_rent ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right"><UtilPill pct={a.utilization_pct || 0} /></td>
                      <td className="px-4 py-2.5 text-right text-white font-medium">{fmt(a.total_revenue || 0)}</td>
                      <td className="px-4 py-2.5 text-right text-fieldiq-muted">{fmt(a.avg_daily_rate || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-fieldiq-accent border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
}
